import React, { useEffect, useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Plus,
  X,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Calculator,
  History,
} from 'lucide-react';

import { DateField } from '../shared/DateField';

/**
 * /alternatives/secondary-purchases
 *
 * PE Phase 4 deliverable 4 — secondary purchase pricing & ledger.
 *
 * <p>Hybrid landing + detail UX:
 * <ul>
 *   <li><b>Landing</b> (PGIM-style): portfolio picker, KPI strip, table of
 *       SECONDARY cost-basis rows for the selected portfolio.</li>
 *   <li><b>Slide-over</b> (Aladdin-style): "+ New Secondary" opens a right-side
 *       form with sections — Buyer & Fund, Inherited slot, Pricing (live preview),
 *       Counterparty, Notes. Submit creates the commitment + DRAFT trade +
 *       SECONDARY cost-basis row through the unified posting engine.</li>
 * </ul>
 */

interface PrivateFund {
  id: number;
  fundName: string;
  managerName: string;
  vintageYear: number;
  fundType: string;
  totalCommittedCapital: number;
  baseCurrency: string;
}

interface Portfolio {
  id: number;
  portfolioId: number;
  portfolioName: string;
}

interface CostBasisRow {
  id: number;
  commitmentId: number;
  portfolioId: number;
  acquisitionType: 'PRIMARY' | 'SECONDARY';
  acquisitionDate: string;
  costBasisAmount: number;
  costBasisCurrency: string;
  navAtPurchase: number | null;
  discountPremiumPct: number | null;
  carryAlreadyPaidAdj: number | null;
  inheritedCalledAmount: number | null;
  inheritedDistributedAmount: number | null;
  inheritedUnfundedAmount: number | null;
  sellerName: string | null;
  sellerLpId: number | null;
  tradeTransactionId: number | null;
  adjustmentToId: number | null;
  adjustmentReason: string | null;
  notes: string | null;
  createdAt: string;
}

const fmtMoney = (v: number | null | undefined) => {
  if (v == null || isNaN(v as number)) return '—';
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1e6) return `${n < 0 ? '-' : ''}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${n < 0 ? '-' : ''}$${(a / 1e3).toFixed(0)}K`;
  return `${n < 0 ? '-' : ''}$${a.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const fmtPctSigned = (decimal: number | null | undefined) => {
  if (decimal == null || isNaN(decimal as number)) return '—';
  const pct = Number(decimal) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

const SecondaryPurchaseScreen: React.FC = () => {
  const navigate = useNavigate();

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [funds, setFunds] = useState<PrivateFund[]>([]);
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [rows, setRows] = useState<CostBasisRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null);
  const [showForm, setShowForm] = useState(false);

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg });
    window.setTimeout(() => setToast(null), 4500);
  };

  // Load portfolios + funds once
  useEffect(() => {
    axios
      .get('/api/dimensions/portfolios')
      .then((r) => {
        const list = r.data?.content || r.data || [];
        const norm: Portfolio[] = list.map((p: any) => ({
          id: p.id ?? p.portfolioId,
          portfolioId: p.portfolioId ?? p.id,
          portfolioName: p.portfolioName || p.name || `Portfolio #${p.id}`,
        }));
        setPortfolios(norm);
        if (norm.length && portfolioId == null) setPortfolioId(norm[0].id);
      })
      .catch(() => {
        /* leave empty; the screen still works once a portfolio is chosen */
      });
    axios
      .get('/api/alternatives/funds')
      .then((r) => setFunds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFunds([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load rows when portfolio changes
  const loadRows = (pid: number) => {
    setLoading(true);
    setError(null);
    axios
      .get(`/api/alternatives/secondary-purchases/portfolio/${pid}/secondaries`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((err) =>
        setError(err?.response?.data?.message || err?.message || 'Failed to load secondaries')
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (portfolioId != null) loadRows(portfolioId);
  }, [portfolioId]);

  // KPIs
  const kpis = useMemo(() => {
    const totalCost = rows.reduce((s, r) => s + (r.costBasisAmount || 0), 0);
    const totalNav = rows.reduce((s, r) => s + (r.navAtPurchase || 0), 0);
    const totalUnfunded = rows.reduce((s, r) => s + (r.inheritedUnfundedAmount || 0), 0);
    const wtdDiscount =
      totalNav > 0
        ? rows.reduce(
            (s, r) => s + (r.navAtPurchase || 0) * (r.discountPremiumPct || 0),
            0
          ) / totalNav
        : 0;
    const carryAdj = rows.reduce((s, r) => s + (r.carryAlreadyPaidAdj || 0), 0);
    const distinctFunds = new Set(rows.map((r) => r.commitmentId)).size;
    return {
      count: rows.length,
      totalCost,
      totalNav,
      totalUnfunded,
      wtdDiscount,
      carryAdj,
      distinctFunds,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-6 py-6">
      <div className="max-w-[1400px] mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">
              Secondary Market
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              LP Secondary Purchases
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-3xl">
              Acquire LP interests from existing investors. Cost basis is the price you{' '}
              <em>actually paid</em> (NAV × (1 + discount/premium) − carry adjustment), kept
              separate from the GP-level commitment slot. Each purchase routes through the unified
              IBOR — DRAFT trade, pre-clear, post — alongside an append-only SECONDARY cost-basis
              row.
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            disabled={portfolioId == null}
            className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-lg shadow-violet-600/20"
          >
            <Plus className="w-4 h-4" />
            New Secondary
          </button>
        </div>

        {/* Portfolio picker */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl px-5 py-4 mb-5 flex items-center gap-4 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Portfolio
          </div>
          <select
            value={portfolioId ?? ''}
            onChange={(e) => setPortfolioId(e.target.value ? Number(e.target.value) : null)}
            className="bg-slate-950 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500"
          >
            <option value="">— Select portfolio —</option>
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>
                P{p.portfolioId} • {p.portfolioName}
              </option>
            ))}
          </select>
          {portfolioId != null && (
            <button
              onClick={() => loadRows(portfolioId)}
              className="text-xs text-slate-400 hover:text-white"
            >
              Reload
            </button>
          )}
        </div>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg px-4 py-3 mb-4 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {toast && (
          <div
            className={`fixed top-20 right-6 z-[60] rounded-lg px-4 py-3 text-sm shadow-2xl border ${
              toast.tone === 'success'
                ? 'bg-emerald-900/90 border-emerald-500/40 text-emerald-100'
                : 'bg-rose-900/90 border-rose-500/40 text-rose-100'
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <Kpi label="Secondaries" value={kpis.count.toString()} />
          <Kpi label="Distinct slots" value={kpis.distinctFunds.toString()} />
          <Kpi label="Cost basis" value={fmtMoney(kpis.totalCost)} />
          <Kpi label="NAV at entry" value={fmtMoney(kpis.totalNav)} />
          <Kpi
            label="Wtd disc/prem"
            value={fmtPctSigned(kpis.wtdDiscount)}
            tone={kpis.wtdDiscount < 0 ? 'positive' : kpis.wtdDiscount > 0 ? 'warning' : 'neutral'}
          />
          <Kpi label="Unfunded inherited" value={fmtMoney(kpis.totalUnfunded)} />
        </div>

        {/* Table */}
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Cost-basis ledger</h2>
            <div className="text-xs text-slate-500">
              {rows.length} SECONDARY row{rows.length === 1 ? '' : 's'}
            </div>
          </div>
          {loading ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">Loading…</div>
          ) : portfolioId == null ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">
              Select a portfolio above to see its secondary holdings.
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-slate-500 text-sm">
              No secondary purchases yet. Click <b>+ New Secondary</b> to record one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-950/40">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left px-5 py-2 font-semibold">Acq date</th>
                    <th className="text-left px-3 py-2 font-semibold">Commit</th>
                    <th className="text-left px-3 py-2 font-semibold">Trade</th>
                    <th className="text-right px-3 py-2 font-semibold">NAV @ purch</th>
                    <th className="text-right px-3 py-2 font-semibold">Disc/Prem</th>
                    <th className="text-right px-3 py-2 font-semibold">Carry adj</th>
                    <th className="text-right px-3 py-2 font-semibold">Cost basis</th>
                    <th className="text-right px-3 py-2 font-semibold">Inh. unfunded</th>
                    <th className="text-left px-3 py-2 font-semibold">Seller</th>
                    <th className="text-left px-5 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const isAmendment = r.adjustmentToId != null;
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-white/5 hover:bg-white/5"
                      >
                        <td className="px-5 py-2.5 text-slate-200">
                          {new Date(r.acquisitionDate).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2.5 text-slate-300">#{r.commitmentId}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-[11px]">
                          {r.tradeTransactionId ? `T${r.tradeTransactionId}` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-300 font-mono">
                          {fmtMoney(r.navAtPurchase)}
                        </td>
                        <td
                          className={`px-3 py-2.5 text-right font-mono font-semibold ${
                            (r.discountPremiumPct || 0) < 0
                              ? 'text-emerald-300'
                              : (r.discountPremiumPct || 0) > 0
                              ? 'text-amber-300'
                              : 'text-slate-400'
                          }`}
                        >
                          <span className="inline-flex items-center gap-1 justify-end">
                            {(r.discountPremiumPct || 0) < 0 ? (
                              <TrendingDown className="w-3 h-3" />
                            ) : (r.discountPremiumPct || 0) > 0 ? (
                              <TrendingUp className="w-3 h-3" />
                            ) : null}
                            {fmtPctSigned(r.discountPremiumPct)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-300 font-mono">
                          {fmtMoney(r.carryAlreadyPaidAdj)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-white font-mono font-semibold">
                          {fmtMoney(r.costBasisAmount)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-400 font-mono">
                          {fmtMoney(r.inheritedUnfundedAmount)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-300 text-[11px]">
                          {r.sellerName || '—'}
                        </td>
                        <td className="px-5 py-2.5">
                          {isAmendment ? (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                              <History className="w-3 h-3" />
                              Amend → #{r.adjustmentToId}
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">
                              Original
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 text-xs text-slate-500 max-w-3xl">
          <strong className="text-slate-400">How it works.</strong> Cost basis ={' '}
          <code className="bg-slate-800 px-1 py-0.5 rounded text-slate-300">
            NAV × (1 + discount/premium) − carry already paid
          </code>
          . Negative discount = you bought at a discount to NAV (good); positive = premium.
          The cost-basis row is append-only — amendments create a new row pointing back at the
          original via <code>adjustment_to_id</code>. The commitment slot, called/distributed
          balances, and any future capital calls continue to ride on the underlying commitment.
        </div>
      </div>

      {showForm && portfolioId != null && (
        <NewSecondaryForm
          portfolioId={portfolioId}
          funds={funds}
          onClose={() => setShowForm(false)}
          onCreated={(msg) => {
            flash('success', msg);
            setShowForm(false);
            loadRows(portfolioId);
          }}
          onError={(msg) => flash('error', msg)}
        />
      )}
    </div>
  );
};

// ---- Slide-over form ----------------------------------------------------------------

interface FormProps {
  portfolioId: number;
  funds: PrivateFund[];
  onClose: () => void;
  onCreated: (msg: string) => void;
  onError: (msg: string) => void;
}

/** Matches `SecondaryPurchaseService.previewPricing` map keys. */
interface PreviewResp {
  navAtPurchase: number;
  discountPremiumPct: number;
  carryAlreadyPaidAdj: number;
  navAdjustedForDiscount: number;
  computedCostBasis: number;
  effectiveDiscountPct: number;
  usingOverride: boolean;
}

const NewSecondaryForm: React.FC<FormProps> = ({
  portfolioId,
  funds,
  onClose,
  onCreated,
  onError,
}) => {
  const today = getTodayDate();

  const [fundId, setFundId] = useState<number | ''>('');
  const [acquisitionDate, setAcquisitionDate] = useState(today);
  const [currency, setCurrency] = useState('USD');
  const [commitmentAmount, setCommitmentAmount] = useState<string>('');
  const [inheritedCalled, setInheritedCalled] = useState<string>('');
  const [inheritedDistributed, setInheritedDistributed] = useState<string>('');
  const [inheritedUnfunded, setInheritedUnfunded] = useState<string>('');
  const [navAtPurchase, setNavAtPurchase] = useState<string>('');
  const [discountPct, setDiscountPct] = useState<string>(''); // user enters e.g. -10 for 10% discount
  const [carryAdj, setCarryAdj] = useState<string>('');
  const [costBasisOverride, setCostBasisOverride] = useState<string>('');
  const [sellerName, setSellerName] = useState('');
  const [sellerLpId, setSellerLpId] = useState<string>('');
  const [notes, setNotes] = useState('');

  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const num = (s: string): number | null => {
    if (!s.trim()) return null;
    const n = Number(s.replace(/[$,]/g, ''));
    return isFinite(n) ? n : null;
  };

  // Auto-derive unfunded if user fills commitment + called and leaves unfunded blank
  useEffect(() => {
    if (!inheritedUnfunded && commitmentAmount && inheritedCalled) {
      const c = num(commitmentAmount);
      const k = num(inheritedCalled);
      if (c != null && k != null && c >= k) {
        setInheritedUnfunded(String(c - k));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitmentAmount, inheritedCalled]);

  // Live pricing preview — debounced
  useEffect(() => {
    const nav = num(navAtPurchase);
    if (nav == null) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const timer = setTimeout(() => {
      const dp = num(discountPct);
      const body: any = {
        portfolioId,
        fundId: fundId || 0,
        acquisitionDate,
        currency,
        navAtPurchase: nav,
        discountPremiumPct: dp == null ? null : dp / 100,
        carryAlreadyPaidAdj: num(carryAdj) || 0,
        costBasisOverride: num(costBasisOverride),
        commitmentAmount: num(commitmentAmount),
        inheritedCalledAmount: num(inheritedCalled),
        inheritedDistributedAmount: num(inheritedDistributed),
        inheritedUnfundedAmount: num(inheritedUnfunded),
      };
      axios
        .post('/api/alternatives/secondary-purchases/preview', body)
        .then((r) => {
          setPreview(r.data as PreviewResp);
          setPreviewError(null);
        })
        .catch((err) => {
          setPreview(null);
          setPreviewError(
            err?.response?.data?.message || err?.message || 'Preview failed'
          );
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [
    navAtPurchase,
    discountPct,
    carryAdj,
    costBasisOverride,
    portfolioId,
    fundId,
    acquisitionDate,
    currency,
    commitmentAmount,
    inheritedCalled,
    inheritedDistributed,
    inheritedUnfunded,
  ]);

  const validate = (): string | null => {
    if (!fundId) return 'Pick a fund.';
    if (!num(commitmentAmount) || num(commitmentAmount)! <= 0)
      return 'Commitment amount required.';
    if (!num(navAtPurchase) || num(navAtPurchase)! < 0) return 'NAV at purchase required.';
    if (num(carryAdj) != null && num(carryAdj)! < 0)
      return 'Carry adjustment must be non-negative.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      onError(err);
      return;
    }
    setSubmitting(true);
    const dp = num(discountPct);
    const body: any = {
      portfolioId,
      fundId: Number(fundId),
      acquisitionDate,
      currency,
      commitmentAmount: num(commitmentAmount),
      inheritedCalledAmount: num(inheritedCalled),
      inheritedDistributedAmount: num(inheritedDistributed),
      inheritedUnfundedAmount: num(inheritedUnfunded),
      navAtPurchase: num(navAtPurchase),
      discountPremiumPct: dp == null ? null : dp / 100,
      carryAlreadyPaidAdj: num(carryAdj) || 0,
      costBasisOverride: num(costBasisOverride),
      sellerName: sellerName.trim() || null,
      sellerLpId: num(sellerLpId),
      notes: notes.trim() || null,
    };
    try {
      const r = await axios.post('/api/alternatives/secondary-purchases', body);
      const data = r.data;
      onCreated(
        `Secondary booked — commitment #${data.commitmentId}, trade T${
          data.tradeTransactionId ?? '—'
        }, cost basis ${fmtMoney(data.costBasisAmount)}.`
      );
    } catch (e: any) {
      onError(e?.response?.data?.message || e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
        aria-hidden
      />
      {/* Slide-over */}
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-[600px] bg-slate-950 border-l border-white/10 z-50 overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-slate-950 border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-400 font-bold">
              New secondary purchase
            </div>
            <div className="text-base font-bold text-white">P{portfolioId}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Buyer & Fund */}
          <Section title="Buyer & Fund">
            <Field label="Fund" required>
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value ? Number(e.target.value) : '')}
                className="input"
              >
                <option value="">— Select fund —</option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.fundName} • {f.managerName} • {f.vintageYear}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Acquisition date" required>
                <DateField
                  value={acquisitionDate ?? ''}
                  onChange={iso => setAcquisitionDate(iso)}
                />
              </Field>
              <Field label="Currency">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="input"
                >
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                  <option>JPY</option>
                  <option>CAD</option>
                </select>
              </Field>
            </div>
            <Field label="Commitment slot size" required>
              <input
                type="text"
                value={commitmentAmount}
                onChange={(e) => setCommitmentAmount(e.target.value)}
                placeholder="e.g. 25,000,000"
                className="input"
              />
            </Field>
          </Section>

          {/* Inherited slot state */}
          <Section
            title="Inherited slot snapshot"
            help="What the seller had drawn / been distributed when you bought the slot."
          >
            <div className="grid grid-cols-3 gap-3">
              <Field label="Called">
                <input
                  type="text"
                  value={inheritedCalled}
                  onChange={(e) => setInheritedCalled(e.target.value)}
                  placeholder="0"
                  className="input"
                />
              </Field>
              <Field label="Distributed">
                <input
                  type="text"
                  value={inheritedDistributed}
                  onChange={(e) => setInheritedDistributed(e.target.value)}
                  placeholder="0"
                  className="input"
                />
              </Field>
              <Field label="Unfunded">
                <input
                  type="text"
                  value={inheritedUnfunded}
                  onChange={(e) => setInheritedUnfunded(e.target.value)}
                  placeholder="auto"
                  className="input"
                />
              </Field>
            </div>
          </Section>

          {/* Pricing */}
          <Section title="Pricing" help="Live cost-basis preview — no save until you click Submit.">
            <div className="grid grid-cols-2 gap-3">
              <Field label="NAV at purchase" required>
                <input
                  type="text"
                  value={navAtPurchase}
                  onChange={(e) => setNavAtPurchase(e.target.value)}
                  placeholder="e.g. 18,400,000"
                  className="input"
                />
              </Field>
              <Field label="Discount/Premium %" help="Negative = discount. e.g. -10 = 10% off NAV.">
                <input
                  type="text"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                  placeholder="-10"
                  className="input"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Carry already paid (adj)">
                <input
                  type="text"
                  value={carryAdj}
                  onChange={(e) => setCarryAdj(e.target.value)}
                  placeholder="0"
                  className="input"
                />
              </Field>
              <Field label="Cost basis override" help="Optional. Skips formula if set.">
                <input
                  type="text"
                  value={costBasisOverride}
                  onChange={(e) => setCostBasisOverride(e.target.value)}
                  placeholder="formula"
                  className="input"
                />
              </Field>
            </div>

            {/* Live preview pane */}
            <div className="mt-3 bg-slate-900/60 border border-violet-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300 font-bold mb-2">
                <Calculator className="w-3.5 h-3.5" />
                Pricing preview
              </div>
              {previewError ? (
                <div className="text-rose-300 text-xs">{previewError}</div>
              ) : !preview ? (
                <div className="text-slate-500 text-xs">
                  Enter NAV at purchase to see pricing.
                </div>
              ) : (() => {
                const nav = Number(preview.navAtPurchase) || 0;
                const dp = Number(preview.discountPremiumPct) || 0;
                const adjusted = Number(preview.navAdjustedForDiscount) || 0;
                const carry = Number(preview.carryAlreadyPaidAdj) || 0;
                const cost = Number(preview.computedCostBasis) || 0;
                const dpAmount = adjusted - nav; // signed: negative for discount
                const formula = preview.usingOverride
                  ? `cost = override = ${fmtMoney(cost)}`
                  : `cost = nav(${fmtMoney(nav)}) × (1 ${dp >= 0 ? '+' : '−'} ${Math.abs(
                      dp * 100
                    ).toFixed(2)}%) − carry(${fmtMoney(carry)}) = ${fmtMoney(cost)}`;
                return (
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                    <PreviewRow label="NAV @ purchase" value={fmtMoney(nav)} />
                    <PreviewRow
                      label="Discount/premium"
                      value={fmtPctSigned(dp)}
                      tone={dp < 0 ? 'positive' : dp > 0 ? 'warning' : 'neutral'}
                    />
                    <PreviewRow
                      label="Disc/prem $"
                      value={fmtMoney(dpAmount)}
                      tone={dpAmount < 0 ? 'positive' : dpAmount > 0 ? 'warning' : 'neutral'}
                    />
                    <PreviewRow
                      label="Carry adj"
                      value={carry > 0 ? `-${fmtMoney(carry)}` : fmtMoney(carry)}
                    />
                    <PreviewRow label="NAV × (1+d/p)" value={fmtMoney(adjusted)} />
                    <PreviewRow
                      label="Cost basis"
                      value={fmtMoney(cost)}
                      bold
                      tone="emphasis"
                    />
                    {preview.usingOverride && (
                      <div className="col-span-2 text-[10px] uppercase tracking-widest text-amber-300 font-bold mt-1">
                        Override active — formula bypassed
                      </div>
                    )}
                    <div className="col-span-2 text-[10px] text-slate-500 font-mono mt-2 break-words">
                      {formula}
                    </div>
                  </div>
                );
              })()}
            </div>
          </Section>

          {/* Counterparty */}
          <Section title="Seller (counterparty)">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Seller name">
                <input
                  type="text"
                  value={sellerName}
                  onChange={(e) => setSellerName(e.target.value)}
                  placeholder="e.g. State Pension X"
                  className="input"
                />
              </Field>
              <Field label="Seller LP id">
                <input
                  type="text"
                  value={sellerLpId}
                  onChange={(e) => setSellerLpId(e.target.value)}
                  placeholder="optional"
                  className="input"
                />
              </Field>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Deal context, broker, restrictions, transfer terms…"
              className="input resize-none"
            />
          </Section>
        </div>

        <div className="sticky bottom-0 bg-slate-950 border-t border-white/10 px-6 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-lg shadow-violet-600/20"
          >
            {submitting ? 'Submitting…' : 'Submit purchase'}
          </button>
        </div>
      </div>

      {/* Tailwind utility class shim (so we can keep markup compact) */}
      <style>{`
        .input {
          width: 100%;
          background: rgb(2 6 23);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.5rem;
          padding: 0.5rem 0.75rem;
          color: white;
          font-size: 0.8125rem;
          outline: none;
        }
        .input:focus { border-color: rgb(139 92 246); }
      `}</style>
    </>
  );
};

const Section: React.FC<{ title: string; help?: string; children: React.ReactNode }> = ({
  title,
  help,
  children,
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
      {title}
    </div>
    {help && <div className="text-[11px] text-slate-500 mb-2">{help}</div>}
    <div className="space-y-3">{children}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}> = ({ label, required, help, children }) => (
  <label className="block">
    <span className="text-[11px] text-slate-400 font-medium">
      {label}
      {required && <span className="text-rose-400 ml-0.5">*</span>}
    </span>
    {help && <span className="block text-[10px] text-slate-500 mb-1">{help}</span>}
    <div className="mt-1">{children}</div>
  </label>
);

const PreviewRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  tone?: 'neutral' | 'positive' | 'warning' | 'emphasis';
}> = ({ label, value, bold, tone = 'neutral' }) => {
  const color =
    tone === 'positive'
      ? 'text-emerald-300'
      : tone === 'warning'
      ? 'text-amber-300'
      : tone === 'emphasis'
      ? 'text-white'
      : 'text-slate-300';
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`${bold ? 'font-bold' : 'font-mono'} ${color}`}>{value}</span>
    </div>
  );
};

const Kpi: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const color =
    tone === 'positive'
      ? 'text-emerald-300'
      : tone === 'warning'
      ? 'text-amber-300'
      : 'text-white';
  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
};

export default SecondaryPurchaseScreen;
